import React from 'react';
import { css } from '@tanium/react-emotion-9';
import { Action, RibbonAction as BaseRibbonAction } from '@tanium/react-ribbon';

export interface ActionArgs<TItem> {
  index: number;
  itemData: TItem;
}

export type ActionEvent<TItem> = ActionArgs<TItem>;

export interface RibbonAction<TItem>
  extends Pick<BaseRibbonAction, 'alwaysVisible' | 'label' | 'icon'> {
  /**
   * Function with `itemData` and `index` as arguments that returns a `boolean`.
   * If it returns `false`, the action will not be available in the ribbon.
   */
  isAvailable?: (args: ActionArgs<TItem>) => boolean;
  /**
   * Callback to invoke when the action is invoked.
   */
  onAction: (event: ActionEvent<TItem>) => void;
}

interface Props<TItem> {
  actions: RibbonAction<TItem>[];
  index: number;
  itemData: TItem;
  isHovering: boolean;
}

const actionsContainerStyle = css({
  width: 0,
});

const actionsWrapperStyle = css({
  float: 'right',
  paddingRight: 16,
  whiteSpace: 'nowrap',
});

const ActionsInner = <TItem extends unknown>({
  actions,
  index,
  itemData,
  isHovering,
}: Props<TItem>) => {
  if (!actions) {
    return null;
  }
  const params: ActionArgs<TItem> = {
    itemData,
    index,
  };
  return (
    <div className={actionsContainerStyle}>
      <div className={actionsWrapperStyle}>
        {actions.map((props, i) => {
          if (props.isAvailable && !props.isAvailable(params)) {
            return null;
          }
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
          const onAction = () => {
            if (props.onAction) {
              props.onAction(params);
            }
          };
          // eslint-disable-next-line react/no-array-index-key
          return <Action key={i} {...props} onAction={onAction} visible={isHovering} />;
        })}
      </div>
    </div>
  );
};

export const Actions = React.memo(ActionsInner);
